// =============================================================================
// catalog-edition-client.http.ts — Adaptateur HTTP RÉEL du CatalogAdminEditionClient.
// =============================================================================
// Câble la LECTURE et l'ÉDITION du contenu d'une version de catalogue derrière le MÊME DI seam
// (CatalogAdminEditionClientProvider). Raison d'être initiale : la page Profils §2.5 v2.2 joint
// FRONT-SIDE `profileCode → {famille, sousFamille}` via `useCatalogProfiles(catalogVersionId)`
// (filtre Famille N1 / Sous-famille N2, C1-bis). Sans client HTTP réel, ce hook lit le STUB par
// défaut (`defaultCatalogAdminEditionClient`) qui ne sert que la version de démo ⇒ pour une campagne
// réelle, aucune famille n'est jointe et le filtre ne s'affiche jamais (divergence ST-CAMPAIGN-02 T2).
//
// PÉRIMÈTRE : cet adaptateur sert (1) les LECTURES de la vue opérationnelle (listProfiles ; listEdges /
// listSkills) ; (2) depuis S20 (DEC-S20-B2), l'UPLOAD Excel A-10 (uploadDryRun / confirmUpload /
// cancelUpload — @Controller('admin/catalog/uploads')) ; et (3) depuis D2a-MUT (S20-REMEDIATION),
// l'ÉDITION profil/matrice (upsertProfile/upsertEdge — PUT @Controller('admin/catalog/versions/:id'))
// + compétences (createSkill POST / updateSkill PATCH / setSkillFamilies PUT / archiveSkill POST —
// @Controller('admin/catalog/skills')). Toutes les mutations frappent l'API réelle.
//
// AUTORITÉ = API (NestJS, CatalogUploadController / CatalogEditionController / CatalogSkillController,
// GLOBAL). Session admin = cookie httpOnly (credentials: 'include'). Les LECTURES sont des GET (aucun
// CSRF) ; les MUTATIONS (POST/PUT/PATCH) portent l'en-tête double-submit `x-csrf-token` = cookie
// non-httpOnly `admin_csrf` (parité HttpCampaignClient). Le secret de session httpOnly n'est JAMAIS
// lu par le SPA. inv.1/inv.2/inv.3/inv.4 (unicité, édition réservée DRAFT, poids [0,1]) = autorité API.

import {
  CatalogApiError,
  type CatalogAdminEditionClient,
  type CatalogVersionDto,
  type ConfirmUploadResult,
  type CorrelationEdgeView,
  type CreateSkillInputDto,
  type EditCorrelationEdgeInputDto,
  type EditProfileInputDto,
  type ProfileCampaignUsageEntry,
  type ProfileView,
  type SkillView,
  type UpdateSkillInputDto,
  type UploadDryRunPreview,
} from '@/api/catalog-client';

/** Forme attendue d'un corps d'erreur structuré (NestJS HttpException). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly message?: string;
  /** Code machine STABLE (ex. A-CATALOG §2.4 : CATALOG_VERSION_WRONG_STATE / PROFILE_ALREADY_ARCHIVED). */
  readonly code?: string;
}

/** Nom du cookie CSRF NON-httpOnly posé au login admin (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'admin_csrf';

/**
 * Lit la valeur du cookie CSRF `admin_csrf` (NON-httpOnly, lisible par le SPA même origine).
 * `undefined` si absent (pas de session, ou contexte sans document) — l'appel mutant partira
 * alors sans en-tête et l'API répondra 403 (fail-closed côté serveur, jamais d'écriture).
 * Parité stricte avec HttpCampaignClient (campaign-client.http.ts).
 */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel (LECTURE) : `fetch` contre l'API admin catalog. `baseUrl` par défaut vide
 * (chemins relatifs : SPA et API partagent l'origine, ou un reverse proxy route `/admin/*`). En
 * test, on injecte un baseUrl ou on mocke `fetch`.
 */
export class HttpCatalogAdminEditionClient implements CatalogAdminEditionClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  /** Construit l'URL absolue (baseUrl + chemin relatif racine API). */
  private url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  /**
   * Exécute une requête et renvoie le JSON typé (ou `undefined` si `expectBody=false`). Lève une
   * CatalogApiError mappée sur réponse non-OK.
   *
   * CSRF DOUBLE-SUBMIT (parité HttpCampaignClient) : sur les méthodes MUTANTES (POST/PUT/PATCH/DELETE),
   * l'AdminSessionGuard exige l'en-tête `x-csrf-token` = cookie non-httpOnly `admin_csrf` (posé au login).
   * Le SPA le lit (même origine) et le ré-émet ; sans lui, toute écriture admin est 403 (fail-closed).
   * Le secret de session httpOnly n'est JAMAIS lu ici.
   *
   * MULTIPART : si `init.body` est un `FormData` (upload Excel), l'en-tête `content-type` est OMIS pour
   * laisser le navigateur poser le boundary `multipart/form-data` lui-même.
   */
  private async request<T>(
    path: string,
    init: RequestInit,
    expectBody: boolean,
  ): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase();
    const isFormData =
      typeof FormData !== 'undefined' && init.body instanceof FormData;
    const headers: Record<string, string> = isFormData
      ? {}
      : { 'content-type': 'application/json' };
    if (method !== 'GET' && method !== 'HEAD') {
      const csrf = readCsrfToken();
      if (csrf !== undefined) {
        headers['x-csrf-token'] = csrf;
      }
    }

    const response = await fetch(this.url(path), {
      // Session admin = cookie httpOnly : transmis avec credentials: 'include'.
      credentials: 'include',
      ...init,
      headers: { ...headers, ...init.headers },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    if (!expectBody) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  /** Exécute un GET admin (cookie de session) et renvoie le JSON typé, ou lève une erreur mappée. */
  private get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'GET' }, true);
  }

  /** Convertit une réponse non-OK en erreur applicative typée (message serveur tel quel). */
  private async toApiError(response: Response): Promise<CatalogApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur le catalogue.`;
    // Le `code` machine STABLE du corps structuré (A-CATALOG §2.4) est propagé pour que l'UI
    // branche dessus (message métier précis) plutôt que sur le message serveur (non stable).
    return new CatalogApiError(message, body?.code, response.status);
  }

  // --- A-09 profil + matrice (LECTURE) ---

  public listProfiles(
    catalogVersionId: string,
  ): Promise<readonly ProfileView[]> {
    return this.get<readonly ProfileView[]>(
      `/admin/catalog/versions/${encodeURIComponent(catalogVersionId)}/profiles`,
    );
  }

  public listEdges(
    catalogVersionId: string,
  ): Promise<readonly CorrelationEdgeView[]> {
    return this.get<readonly CorrelationEdgeView[]>(
      `/admin/catalog/versions/${encodeURIComponent(catalogVersionId)}/correlation-edges`,
    );
  }

  /**
   * GET /admin/catalog/versions/:id/profiles/:profileCode/campaign-usage — campagnes EN COURS référençant
   * le profil (avertissement d'archivage §2.4, A0R-10). LECTURE SEULE (aucun CSRF) ; `[]` si aucune (jamais
   * 404). INFORMATIONNEL : la réponse n'implique aucun blocage côté écran.
   */
  public listProfileCampaignUsage(
    catalogVersionId: string,
    profileCode: string,
  ): Promise<readonly ProfileCampaignUsageEntry[]> {
    return this.get<readonly ProfileCampaignUsageEntry[]>(
      `/admin/catalog/versions/${encodeURIComponent(catalogVersionId)}/profiles/${encodeURIComponent(
        profileCode,
      )}/campaign-usage`,
    );
  }

  // --- A-25 compétences (LECTURE) ---

  public listSkills(): Promise<readonly SkillView[]> {
    return this.get<readonly SkillView[]>(`/admin/catalog/skills`);
  }

  /** REM-A #1 : GET /admin/catalog/skills/families — référentiel des familles associables (DISTINCT). */
  public listAssociableFamilies(): Promise<readonly string[]> {
    return this.get<readonly string[]>(`/admin/catalog/skills/families`);
  }

  /**
   * REM-A #3 : GET /admin/skills/matrix-export.csv — MATRICE d'associations (contenu CSV brut, durci API).
   * Route figée `admin/skills/...` (contrôleur d'export dédié). Réponse `text/csv` : on lit le TEXTE (pas
   * de JSON). Session admin (cookie httpOnly) transmise par `credentials: 'include'`.
   */
  public async exportSkillsMatrixCsv(): Promise<string> {
    const response = await fetch(this.url(`/admin/skills/matrix-export.csv`), {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return response.text();
  }

  // --- A-08 PROMOTION DRAFT→PUBLISHED (DBU §2.4) — POST, mutation admin ---

  /**
   * POST /admin/catalog/versions/:id/publish — promotion DRAFT→PUBLISHED. Mutation ⇒ en-tête CSRF
   * double-submit posé par `request` (méthode ≠ GET). Renvoie la vue de la version publiée. L'API
   * refuse (409) une version qui n'est pas en Brouillon ; l'erreur mappée remonte telle quelle.
   */
  public publishVersion(catalogVersionId: string): Promise<CatalogVersionDto> {
    return this.request<CatalogVersionDto>(
      `/admin/catalog/versions/${encodeURIComponent(catalogVersionId)}/publish`,
      { method: 'POST' },
      true,
    );
  }

  // --- A-09 ÉDITION profil + matrice (CÂBLÉ D2a-MUT) — PUT, version DRAFT ---

  /**
   * PUT /admin/catalog/versions/:id/profiles — upsert d'un Profile dans une version DRAFT (inv.1/inv.2
   * appliqués par le service). `profileCode` absent ⇒ création ; présent ⇒ mise à jour idempotente.
   */
  public upsertProfile(
    catalogVersionId: string,
    input: EditProfileInputDto,
  ): Promise<ProfileView> {
    return this.request<ProfileView>(
      `/admin/catalog/versions/${encodeURIComponent(catalogVersionId)}/profiles`,
      { method: 'PUT', body: JSON.stringify(input) },
      true,
    );
  }

  /**
   * POST /admin/catalog/versions/:id/profiles/:profileCode/archive — archive un Profile sur une
   * version DRAFT (A-CATALOG §2.4). Mutation ⇒ CSRF double-submit posé par `request`. L'API refuse
   * (409 STRUCTURÉ) une version non-DRAFT (`CATALOG_VERSION_WRONG_STATE`) ou un profil déjà archivé
   * (`PROFILE_ALREADY_ARCHIVED`) ; l'erreur mappée (avec `code`) remonte telle quelle. Aucun body.
   */
  public archiveProfile(
    catalogVersionId: string,
    profileCode: string,
  ): Promise<ProfileView> {
    return this.request<ProfileView>(
      `/admin/catalog/versions/${encodeURIComponent(catalogVersionId)}/profiles/${encodeURIComponent(profileCode)}/archive`,
      { method: 'POST' },
      true,
    );
  }

  /**
   * POST /admin/catalog/versions/:id/profiles/:profileCode/restore — restaure un Profile archivé
   * (Archived→Active, DBU §2.4 l.960). Mutation ⇒ CSRF double-submit. 409 STRUCTURÉ
   * (`CATALOG_VERSION_WRONG_STATE` / `PROFILE_NOT_ARCHIVED`) remonte avec son `code`. Aucun body.
   */
  public restoreProfile(
    catalogVersionId: string,
    profileCode: string,
  ): Promise<ProfileView> {
    return this.request<ProfileView>(
      `/admin/catalog/versions/${encodeURIComponent(catalogVersionId)}/profiles/${encodeURIComponent(profileCode)}/restore`,
      { method: 'POST' },
      true,
    );
  }

  /**
   * POST /admin/catalog/versions/:id/profiles/:profileCode/clone — clone un Profile (code neuf, contenu
   * copié, 0 arête, DBU §2.4 l.950). Mutation ⇒ CSRF double-submit. Renvoie la vue du clone. Aucun body.
   */
  public cloneProfile(
    catalogVersionId: string,
    profileCode: string,
  ): Promise<ProfileView> {
    return this.request<ProfileView>(
      `/admin/catalog/versions/${encodeURIComponent(catalogVersionId)}/profiles/${encodeURIComponent(profileCode)}/clone`,
      { method: 'POST' },
      true,
    );
  }

  /**
   * POST /admin/catalog/versions/:id/profiles/:sourceProfileCode/merge — FUSIONNE la source dans la cible
   * (R15-CAT-01, DBU §2.4 l.866). Mutation ⇒ CSRF double-submit. Corps JSON `{ targetProfileCode,
   * confirmationCode }`. 409/400 STRUCTURÉS (CATALOG_VERSION_WRONG_STATE / PROFILE_MERGE_*) remontent avec
   * leur `code`. Renvoie la vue du profil CIBLE (survivant, relu).
   */
  public mergeProfiles(
    catalogVersionId: string,
    sourceProfileCode: string,
    targetProfileCode: string,
    confirmationCode: string,
  ): Promise<ProfileView> {
    return this.request<ProfileView>(
      `/admin/catalog/versions/${encodeURIComponent(catalogVersionId)}/profiles/${encodeURIComponent(sourceProfileCode)}/merge`,
      { method: 'POST', body: JSON.stringify({ targetProfileCode, confirmationCode }) },
      true,
    );
  }

  /** GET /admin/catalog/versions/:id/profiles/suggest-code — code de profil candidat (DBU §2.4 l.952). */
  public suggestProfileCode(
    catalogVersionId: string,
  ): Promise<{ readonly profileCode: string }> {
    return this.get<{ readonly profileCode: string }>(
      `/admin/catalog/versions/${encodeURIComponent(catalogVersionId)}/profiles/suggest-code`,
    );
  }

  /** GET /admin/catalog/versions/:id/profiles/:profileCode — lit un Profile (visualiser, DBU §2.4 l.950). */
  public getProfile(
    catalogVersionId: string,
    profileCode: string,
  ): Promise<ProfileView> {
    return this.get<ProfileView>(
      `/admin/catalog/versions/${encodeURIComponent(catalogVersionId)}/profiles/${encodeURIComponent(profileCode)}`,
    );
  }

  /**
   * PUT /admin/catalog/versions/:id/correlation-edges — upsert d'une arête (poids ∈ [0,1]) dans une
   * DRAFT (inv.2 + validation du poids appliqués par le service). `edgeId` absent ⇒ création.
   */
  public upsertEdge(
    catalogVersionId: string,
    input: EditCorrelationEdgeInputDto,
  ): Promise<CorrelationEdgeView> {
    return this.request<CorrelationEdgeView>(
      `/admin/catalog/versions/${encodeURIComponent(catalogVersionId)}/correlation-edges`,
      { method: 'PUT', body: JSON.stringify(input) },
      true,
    );
  }

  // --- A-25 ÉDITION compétences (CÂBLÉ D2a-MUT) — @Controller('admin/catalog/skills') ---

  /** POST /admin/catalog/skills — crée une compétence (inv.1) + DE.SkillCreated. */
  public createSkill(input: CreateSkillInputDto): Promise<SkillView> {
    return this.request<SkillView>(
      '/admin/catalog/skills',
      { method: 'POST', body: JSON.stringify(input) },
      true,
    );
  }

  /** PATCH /admin/catalog/skills/:skillCode — MAJ partielle (libellé/description/catégorie/état). */
  public updateSkill(
    skillCode: string,
    input: UpdateSkillInputDto,
  ): Promise<SkillView> {
    return this.request<SkillView>(
      `/admin/catalog/skills/${encodeURIComponent(skillCode)}`,
      { method: 'PATCH', body: JSON.stringify(input) },
      true,
    );
  }

  /**
   * PUT /admin/catalog/skills/:skillCode/familles — réconcilie l'ensemble des familles associées vers
   * la CIBLE fournie (associate/dissociate). Le back lit `body.familles` ⇒ corps `{ familles }`.
   */
  public setSkillFamilies(
    skillCode: string,
    familles: readonly string[],
  ): Promise<SkillView> {
    return this.request<SkillView>(
      `/admin/catalog/skills/${encodeURIComponent(skillCode)}/familles`,
      { method: 'PUT', body: JSON.stringify({ familles }) },
      true,
    );
  }

  /** POST /admin/catalog/skills/:skillCode/archive — archive (state=Archived, associations préservées). */
  public archiveSkill(skillCode: string): Promise<SkillView> {
    return this.request<SkillView>(
      `/admin/catalog/skills/${encodeURIComponent(skillCode)}/archive`,
      { method: 'POST' },
      true,
    );
  }

  /**
   * GET /admin/catalog/skills/:skillCode/campaign-usage — campagnes EN COURS où la compétence est PROPOSABLE
   * (avertissement d'archivage R15-CAT-03, DEC-27). LECTURE SEULE (aucun CSRF) ; `[]` si aucune (jamais 404).
   * INFORMATIONNEL : la réponse n'implique aucun blocage côté écran.
   */
  public listSkillCampaignUsage(
    skillCode: string,
  ): Promise<readonly ProfileCampaignUsageEntry[]> {
    return this.get<readonly ProfileCampaignUsageEntry[]>(
      `/admin/catalog/skills/${encodeURIComponent(skillCode)}/campaign-usage`,
    );
  }

  /**
   * POST /admin/catalog/skills/:skillCode/restore — RESTAURE une compétence archivée (Archivé → Actif,
   * DBU §2.10 l.2256 ; #16). Primitive DÉDIÉE : émet DE.SkillRestored côté serveur. 409 `SKILL_NOT_ARCHIVED`
   * remonte en `CatalogApiError`.
   */
  public restoreSkill(skillCode: string): Promise<SkillView> {
    return this.request<SkillView>(
      `/admin/catalog/skills/${encodeURIComponent(skillCode)}/restore`,
      { method: 'POST' },
      true,
    );
  }

  // --- A-10 upload Excel (CÂBLÉ S20, DEC-S20-B2) — @Controller('admin/catalog/uploads') ---

  /**
   * POST /admin/catalog/uploads — upload + DRY-RUN. Le fichier part en `multipart/form-data`, champ
   * `file` (impératif : back `FileInterceptor('file')`). AUCUNE version produite (outcome=Pending).
   */
  public uploadDryRun(file: File): Promise<UploadDryRunPreview> {
    const form = new FormData();
    form.append('file', file);
    return this.request<UploadDryRunPreview>(
      '/admin/catalog/uploads',
      { method: 'POST', body: form },
      true,
    );
  }

  /** POST /admin/catalog/uploads/:attemptId/confirm — promotion ATOMIQUE en DRAFT (body JSON { versionNumber }). */
  public confirmUpload(
    attemptId: string,
    versionNumber: string,
  ): Promise<ConfirmUploadResult> {
    return this.request<ConfirmUploadResult>(
      `/admin/catalog/uploads/${encodeURIComponent(attemptId)}/confirm`,
      { method: 'POST', body: JSON.stringify({ versionNumber }) },
      true,
    );
  }

  /** POST /admin/catalog/uploads/:attemptId/cancel — annule l'attempt (→ Cancelled ; pas de body). */
  public cancelUpload(
    attemptId: string,
  ): Promise<{ readonly attemptId: string; readonly outcome: 'Cancelled' }> {
    return this.request<{ readonly attemptId: string; readonly outcome: 'Cancelled' }>(
      `/admin/catalog/uploads/${encodeURIComponent(attemptId)}/cancel`,
      { method: 'POST' },
      true,
    );
  }
}
